<template>
    <div id="pagoAcumulativo">
        <b-container>
            <!-- Loading spinner -->
            <b-row v-if="loading">
                <b-col class="text-center">
                    <b-spinner variant="primary" label="Cargando..."></b-spinner>
                </b-col>
            </b-row>

            <!-- Error message -->
            <b-row v-if="error">
                <b-col>
                    <b-alert show variant="danger">
                        {{ error }}
                    </b-alert>
                </b-col>
            </b-row>

            <!-- Orders table -->
            <b-row v-if="!loading && !error && orders.length">
                <b-col class="pt-5 pb-5">
                    <b-card no-body>
                        <template #header>
                            Ordenes de compra pendientes de pago
                        </template>
                        <template #footer>
                            <b-row id="mpagoHolder" align-h="end">
                                <b-col cols="auto" v-if="isUnpaid">
                                    <!-- Botón de referencia de mercado pago -->
                                    <div id="mercadopago-button" class="mercadopago-button"></div>
                                </b-col>
                                <b-col cols="uto" v-else>
                                    <!-- Botón de referencia de mercado pago -->
                                    <b-button variant="secondary" block disabled>
                                        Pedido(s) pagado(s)
                                        <i class="fa fa-check"></i>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </template>
                        <b-table striped hover class="ordenes-table" :items="orders" :fields="fields">
                            <template #bottom-row>
                                <b-td :colspan="2" class="text-right">
                                    <strong>Total:</strong>
                                </b-td>
                                <b-td>
                                    <strong>{{orders.reduce((sum, order) => sum + parseFloat(order.total), 0) |
                                        currency}}</strong>
                                </b-td>
                            </template>
                            <template #cell(total)="row">
                                {{ row.value | currency }}
                            </template>
                            <template #cell(actions)="row">
                                <b-button size="sm" variant="primary" @click="viewDetails(row.item)">
                                    Ver detalles
                                </b-button>
                            </template>
                        </b-table>
                    </b-card>
                    <template v-if="!isUnpaid">
                        <b-alert v-if="isLogged" show variant="success" class="mt-4 mb-0 text-center">
                            <strong>
                                Todos los pedidos han sido pagados.
                            </strong>

                            <br>

                            Pronto nos pondremos en contacto <br>
                            contigo para coordinar la entrega.

                            <hr>

                            <b-button variant="primary" @click="$router.push('/usuario/historial-compras')">
                                Ver mis pedidos
                            </b-button>
                        </b-alert>
                        <b-alert v-else show variant="info" class="mt-4 mb-0 text-center">
                            <b>
                                Te invitamos a ingresar con tu cuenta en RefaApp® para que puedas hacer seguimiento de
                                tus pedidos y compras más fácilmente.
                            </b>
                            <br>
                            Al crear tu cuenta recibirás <strong>promociones exclusivas</strong>, podrás
                            <strong>consultar y administrar tus pedidos</strong> y solicitar ayuda de 
                            manera más sencilla. 
                            <hr>
                            <div class="text-center">
                                <b-button variant="secondary" @click="$router.push('/login')">
                                    Ya tengo cuenta
                                </b-button>
                                <b-button variant="primary" @click="$router.push('/signup')">
                                    ¡Quiero mi cuenta gratuita!
                                </b-button>
                            </div>
                        </b-alert>
                    </template>
                </b-col>
            </b-row>

            <!-- No orders message -->
            <b-row v-if="!loading && !error && !orders.length">
                <b-col>
                    <b-alert show variant="info">
                        No hay pedidos pendientes de pago.
                    </b-alert>
                </b-col>
            </b-row>

        </b-container>

        <!-- Details modal -->
        <b-modal v-model="showModal" title="Detalles del pedido" ok-only>
            <pre>{{ selectedOrder }}</pre>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
import { getMercadoPagoPublicKey } from '@/js/mercadopago.js'
import { mapGetters } from 'vuex'

export default {
    name: 'PagoAcumulativo',
    data() {
        return {
            loading: true,
            error: null,
            orders: [],
            preferenceId: null,
            reviewInterval: null,
            isMPLoaded: false,
            fields: [
                { key: 'numero_orden', label: 'No. de orden' },
                { key: 'fechaentrega', label: 'Fecha de entrega' },
                { key: 'total', label: 'Total' },
                // { key: '', label: '' },
                // { key: 'actions', label: 'Acciones' }
            ],
            showModal: false,
            selectedOrder: null
        }
    },
    watch: {
        isUnpaid(newVal) {
            if (newVal)
                this.loadMercadoPagoJS()
            else 
                this.isMPLoaded = false
        }
    },
    computed: {
        ...mapGetters(['isLogged']),
        isUnpaid() {
            return this.orders.some(order => order.pedido_estatus.indexOf('Pendiente de pago') !== -1)
        }
    },
    methods: {
        async initMercadoPago() {
            try {
                // Obtener el monto total
                const total = this.orders.reduce((sum, order) => sum + parseFloat(order.total), 0)

                // Cargar SDK de MercadoPago
                const mp = new MercadoPago(getMercadoPagoPublicKey(), {
                    locale: 'es-MX'
                })

                // Crear botón de pago
                mp.checkout({
                    preference: {
                        id: this.preferenceId
                    },
                    render: {
                        container: '.mercadopago-button',
                        label: 'Pagar con MercadoPago'
                    },
                })

            } catch (error) {
                this.error = 'Error al inicializar MercadoPago: ' + error.message
            }
        },
        loadMercadoPagoJS() {
            if (this.isMPLoaded) return;
            // Cargar script de MercadoPago
            const script = document.createElement('script')
            script.src = 'https://sdk.mercadopago.com/js/v2'
            script.addEventListener('load', this.initMercadoPago)
            document.body.appendChild(script)
            this.isMPLoaded = true
        },
        async fetchOrders() {
            try {
                const groupingKeyHash = this.$route.params.groupingKeyHash
                const response = await axios.get(`orders/get_group_orders/${groupingKeyHash}`)

                console.log('Respuesta de la API:', response) // Debug: Verificar la respuesta de la API

                if (response.status) {
                    this.orders = response.data.orders;
                    this.preferenceId = response.data.preferenceId;
                } else if (response.msg) {
                    throw response.msg;
                } else {
                    throw 'Error al cargar la configuración de compra'
                }
            } catch (error) {
                this.error = error;
                this.orders = [];
            } finally {
                this.loading = false
            }
        },
        viewDetails(order) {
            this.selectedOrder = order
            this.showModal = true
        },
        // setInterval() {
        //     this.reviewInterval = setInterval(() => {
        //         if (this.isUnpaid) {
        //             this.fetchOrders()
        //         } else {
        //             clearInterval(this.reviewInterval)
        //         }
        //     }, 10000)
        // }
    },
    async created() {
        await this.fetchOrders()
        // this.setInterval()
    }
}
</script>

<style lang="scss">
#mpagoHolder {
    @media (max-width: 576px) {
        &.justify-content-end {
            justify-content: center !important;
        }
    }
}

.limit-width {
    max-width: 400px;
    margin: 0 auto;
    text-align: center;
}

.ordenes-table {
    text-align: center;
}

#pagoAcumulativo {
    padding: 20px;
}
</style>